// ─────────────────────────────────────────────────────────────────────────────
// app/(auth)/login/hooks/useLoginForm.ts
// Yazar: Bilge
//
// Login formunun tüm iş mantığını barındıran custom hook.
// LoginForm.tsx bileşeni sadece UI render eder; bu hook asıl işleri yapar:
//   - react-hook-form + Zod ile form doğrulama
//   - useLoginMutation ile RTK Query üzerinden API çağrısı
//   - Token kaydetme (saveTokens) ve "beni hatırla" yönetimi
//   - Başarılı girişte flash toast + /dashboard yönlendirmesi
// ─────────────────────────────────────────────────────────────────────────────

"use client";

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { loginSchema, LoginFormValues } from "@/app/schemas/auth.schema";
import { useLoginMutation } from "../api";
import { saveTokens, saveRememberedEmail, clearRememberedEmail, getRememberedEmail } from "@/app/lib/auth";
import { setFlashToast } from "@/app/lib/flashToast";

export function useLoginForm() {
  // useLoginMutation: authApi.ts'de tanımlı POST /auth/login endpoint'i
  // isLoading → buton "Giriş yapılıyor..." metnine döner
  const [login, { isLoading: loading }] = useLoginMutation();

  // react-hook-form: Zod şemasıyla doğrulama yapar
  // Development modunda geliştirici kolaylığı için test credentials doldurulur
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",       // Kendi e-posta adresini yaz
      password: "",    // Kendi şifreni yaz
      remember_me: false,
    },
  });

  // Sayfa açılışında "beni hatırla" ile kaydedilmiş e-postayı doldur
  useEffect(() => {
    const savedEmail = getRememberedEmail();
    if (savedEmail) {
      form.setValue("email", savedEmail);
      form.setValue("remember_me", true);
    }
  }, [form]);

  const onSubmit = async (data: LoginFormValues) => {
    try {
      // RTK Query mutation çağrısı — .unwrap() ile hata throw edilmesini sağlar
      const tokens = await login({ email: data.email, password: data.password }).unwrap();

      // Backend'den gelen token'ları cookie'ye yaz
      saveTokens(tokens.access_token, tokens.refresh_token, tokens.expires_in);

      // "Beni hatırla" seçiliyse e-postayı localStorage'a kaydet
      if (data.remember_me) {
        saveRememberedEmail(data.email);
      } else {
        clearRememberedEmail(); // Seçili değilse önceki kaydı temizle
      }

      // window.location.href kullanılıyor çünkü Redux store'u sıfırlamak gerekir.
      // router.push() ile yönlendirme yapılsaydı eski cache kalırdı.
      // setFlashToast: yönlendirme sonrası yeni sayfada toast gösterir
      setFlashToast("Giriş başarılı", "success");
      window.location.href = "/dashboard";
    } catch (err: unknown) {
      // API hatası: backend'den gelen mesajı göster
      const message = err instanceof Error ? err.message : "Giriş yapılırken bir hata oluştu.";
      toast.error(message);
    }
  };

  return { form, loading, onSubmit: form.handleSubmit(onSubmit) };
}